/**
 *
 * モーダル内の動画を遅延読み込みするモジュール。
 * モーダルが開かれるまで `<source data-src="...">` に動画URLを保持し、
 * モーダルが開いた（inert属性が外れた）タイミングで `src` にセットして再生する。
 * モーダルが閉じた（inert属性が付いた）タイミングで動画を停止・srcをリセットする。
 *
 * PHPテンプレート（modal.php）の `<source src="...">` を
 * `<source data-src="...">` に変更しておく必要がある。
 *
 * app.js への追記:
 * ```js
 * import "./module/modal-lazy-video";
 * ```
 */

/**
 * 指定したモーダル要素内の <source data-src> を <source src> に差し替えて動画を読み込み・再生する。
 *
 * @param modal - 対象のモーダル要素
 *
 * @example
 * const modal = document.getElementById('modal-movie-0');
 * if (modal) loadVideoInModal(modal);
 */
const loadVideoInModal = (modal: Element): void => {
    const video = modal.querySelector<HTMLVideoElement>('video');
    if (!video) return;

    const sources = video.querySelectorAll<HTMLSourceElement>('source[data-src]');
    if (sources.length === 0) return;

    let hasNewSrc = false;

    sources.forEach((source) => {
        const dataSrc = source.dataset.src;
        if (dataSrc && source.getAttribute('src') !== dataSrc) {
            source.setAttribute('src', dataSrc);
            hasNewSrc = true;
        }
    });

    if (hasNewSrc) {
        video.load();
    }

    video.play().catch(() => {
        // autoplay policy などで再生できない場合は無視
    });
};

/**
 * 指定したモーダル要素内の動画を停止し、srcをリセットしてバッファを解放する。
 *
 * @param modal - 対象のモーダル要素
 *
 * @example
 * const modal = document.getElementById('modal-movie-0');
 * if (modal) unloadVideoInModal(modal);
 */
const unloadVideoInModal = (modal: Element): void => {
    const video = modal.querySelector<HTMLVideoElement>('video');
    if (!video) return;

    video.pause();
    video.currentTime = 0;

    const sources = video.querySelectorAll<HTMLSourceElement>('source[src]');
    sources.forEach((source) => {
        source.removeAttribute('src');
    });

    video.load();
};

/**
 * モーダルの `inert` 属性の変化を MutationObserver で監視し、
 * 開閉に合わせて動画の遅延読み込み・アンロードを行う。
 *
 * @param modal - 監視対象のモーダル要素
 */
const observeModal = (modal: Element): void => {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.type !== 'attributes' || mutation.attributeName !== 'inert') return;

            const isOpen = !modal.hasAttribute('inert');

            if (isOpen) {
                loadVideoInModal(modal);
            } else {
                unloadVideoInModal(modal);
            }
        });
    });

    observer.observe(modal, {
        attributes: true,
        attributeFilter: ['inert'],
    });
};

/**
 * ページ内の動画付きモーダル（`.modal-opt-video`）をすべて検索し、
 * それぞれに遅延読み込みの監視をセットアップする。
 */
const init = (): void => {
    const modals = document.querySelectorAll<HTMLElement>('.modal-opt-video');
    if (modals.length === 0) return;

    modals.forEach((modal) => {
        observeModal(modal);
    });
};

// DOMContentLoaded 後に自動実行
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}